const MS_PER_DAY = 24 * 60 * 60 * 1_000;
function averageBetween(readings, fromMs, toMs) {
    let sum = 0;
    let count = 0;
    for (const r of readings) {
        const t = Date.parse(r.timestamp);
        if (!Number.isFinite(t) || t < fromMs || t >= toMs)
            continue;
        if (!Number.isFinite(r.value))
            continue;
        sum += r.value;
        count += 1;
    }
    return count === 0 ? null : { avg: sum / count, count };
}
/**
 * Daily / weekly / monthly averages for one parameter, each with the
 * preceding period for comparison. `nowMs` is injectable for tests.
 */
export function summarizePeriods(readings, parameter, nowMs) {
    const mine = readings.filter((r) => r.parameter === parameter);
    const day = MS_PER_DAY;
    const week = 7 * MS_PER_DAY;
    const month = 30 * MS_PER_DAY;
    const last24h = averageBetween(mine, nowMs - day, nowMs);
    const prev24h = averageBetween(mine, nowMs - 2 * day, nowMs - day);
    const last7d = averageBetween(mine, nowMs - week, nowMs);
    const prev7d = averageBetween(mine, nowMs - 2 * week, nowMs - week);
    const last30d = averageBetween(mine, nowMs - month, nowMs);
    const prev30d = averageBetween(mine, nowMs - 2 * month, nowMs - month);
    return {
        last24h,
        prev24h,
        last7d,
        prev7d,
        last30d,
        prev30d,
        delta7d: last7d && prev7d ? last7d.avg - prev7d.avg : null,
        delta30d: last30d && prev30d ? last30d.avg - prev30d.avg : null,
    };
}
/**
 * Compare a manual reading against the nearest Apex point within
 * `toleranceHours`. Returns `null` when no Apex point is close enough.
 */
export function computeDrift(manualValue, manualTsMs, apexPoints, toleranceHours = 12) {
    if (!Number.isFinite(manualValue) || apexPoints.length === 0)
        return null;
    const toleranceMs = toleranceHours * 60 * 60 * 1_000;
    let best = null;
    let bestGap = Number.POSITIVE_INFINITY;
    for (const p of apexPoints) {
        if (!Number.isFinite(p.value))
            continue;
        const gap = Math.abs(p.ts - manualTsMs);
        if (gap < bestGap) {
            best = p;
            bestGap = gap;
        }
    }
    if (!best || bestGap > toleranceMs)
        return null;
    return {
        apexValue: best.value,
        diff: manualValue - best.value,
        timeGapMs: bestGap,
    };
}
/**
 * Which of `parameters` haven't been tested within `thresholdDays`?
 * Returns one entry per overdue parameter, most-overdue first
 * (never-tested entries sort last).
 */
export function staleParameters(readings, parameters, nowMs, thresholdDays = 7) {
    const latest = new Map();
    for (const r of readings) {
        const t = Date.parse(r.timestamp);
        if (!Number.isFinite(t))
            continue;
        const prev = latest.get(r.parameter);
        if (prev === undefined || t > prev)
            latest.set(r.parameter, t);
    }
    const out = [];
    for (const p of parameters) {
        const t = latest.get(p);
        if (t === undefined) {
            out.push({ parameter: p, daysSince: null });
            continue;
        }
        const days = Math.floor((nowMs - t) / MS_PER_DAY);
        if (days >= thresholdDays)
            out.push({ parameter: p, daysSince: days });
    }
    out.sort((a, b) => {
        if (a.daysSince === null && b.daysSince === null)
            return 0;
        if (a.daysSince === null)
            return 1;
        if (b.daysSince === null)
            return -1;
        return b.daysSince - a.daysSince;
    });
    return out;
}
//# sourceMappingURL=insights.js.map